import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlay, FiPause, FiExternalLink, FiClock, FiMusic } = FiIcons;

function Music() {
  const [selectedAlbum, setSelectedAlbum] = useState('super-deluxe');
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [playingTrack, setPlayingTrack] = useState(null);
  const audioRef = useRef(null);

  const albums = {
    'super-deluxe': {
      title: 'SUPER DELUXE',
      year: '2013',
      image: 'https://media.publit.io/file/BigCitySupreme/BigCitySupreme-Supreme-Deluxe.jpg',
      description: 'Our debut masterpiece that put Edmonton on the rock map. Nine tracks of pure auditory gold that\'ll make your neighbors question their life choices.',
      tracks: [
        {
          title: 'Gold Dipped Diamonds',
          duration: '4:07',
          description: 'A fusion of Beastie Boys swagger and rock groove that\'ll make you feel like royalty (the kind that rocks).',
          audioFile: '/media/Gold Dipped Diamonds.mp3'
        },
        {
          title: 'It Feels Good',
          duration: '2:24',
          description: 'Upbeat and infectious opener that proves good vibes are contagious. Side effects may include uncontrollable dancing.',
          audioFile: '/media/It Feels Good.mp3'
        },
        {
          title: 'Until Then',
          duration: '5:52',
          description: 'Our epic journey through expansive soundscapes. Buckle up, it\'s a ride through the Supreme universe.',
          audioFile: '/media/Until Then.mp3'
        },
        {
          title: 'Gone for Good',
          duration: '3:20',
          description: 'Cement-scraped attitude meets 90s rock nostalgia. For when you need to feel dramatically awesome.',
          audioFile: '/media/Gone for Good.mp3'
        },
        {
          title: 'Razor Blade Boomerang',
          duration: '2:55',
          description: 'Sharp, edgy punk flair that cuts both ways. What goes around, rocks around.',
          audioFile: '/media/Razor Blade Boomerang.mp3'
        },
        {
          title: '100 Cigarettes',
          duration: '2:57',
          description: 'Raw and reflective with a gritty vibe. Don\'t worry, no actual cigarettes were harmed in the making.',
          audioFile: '/media/100 Cigarettes.mp3'
        },
        {
          title: 'Hello',
          duration: '2:25',
          description: 'Short, punchy, and playful. Our way of saying "What\'s up?" in the most rock way possible.',
          audioFile: '/media/Hello.mp3'
        },
        {
          title: 'The Upper Hand',
          duration: '4:10',
          description: 'A mix of Weezer-style melody and Chili Peppers funk. We\'ve got the upper hand, and we\'re not afraid to use it.',
          audioFile: '/media/The Upper Hand.mp3'
        },
        {
          title: 'Running Circles',
          duration: '3:11',
          description: 'Energetic and rhythmically tight. Sometimes you run in circles, sometimes the circles run around you.',
          audioFile: '/media/Running Circles.mp3'
        }
      ]
    },
    'urban-dreams': {
      title: 'URBAN DREAMS',
      year: '2018',
      image: 'https://media.publit.io/file/BigCitySupreme/BigCitySupreme-UrbanDreams.png',
      description: 'Our sophomore album that explores the concrete jungle. Ten tracks that capture the essence of city life, from neon nights to midnight rendezvous.',
      tracks: [
        {
          title: 'Concrete Jungle',
          duration: '3:45',
          description: 'A high-energy anthem about the thrills and chaos of city life. Perfect for navigating rush hour traffic.',
          audioFile: '/media/Concrete Jungle.mp3'
        },
        {
          title: 'Neon Nights',
          duration: '4:12',
          description: 'A catchy melody that captures the essence of nightlife and vibrant urban adventures. The city never sleeps, and neither do we.',
          audioFile: '/media/Neon Nights.mp3'
        },
        {
          title: 'Echoes of Yesterday',
          duration: '4:38',
          description: 'A reflective ballad about memories and experiences that linger in the bustling city. Sometimes we get nostalgic while stuck in traffic.',
          audioFile: '/media/Echoes of Yesterday.mp3'
        },
        {
          title: 'Fast Lane',
          duration: '3:28',
          description: 'A driving rock song about living life at full speed and chasing dreams. Warning: may lead to reckless driving (don\'t actually do it).',
          audioFile: '/media/Fast Lane.mp3'
        },
        {
          title: 'Street Symphony',
          duration: '2:56',
          description: 'An instrumental piece that blends various urban sounds, creating a musical representation of city life. We recorded this during rush hour.',
          audioFile: '/media/Street Symphony.mp3'
        },
        {
          title: 'Midnight Rendezvous',
          duration: '3:59',
          description: 'A romantic track about meeting someone special under the stars. Or under the streetlights, because Edmonton has a lot of those.',
          audioFile: '/media/Midnight Rendezvous.mp3'
        },
        {
          title: 'Runaway Heart',
          duration: '4:04',
          description: 'A song depicting the feeling of wanting to escape the ordinary for something extraordinary. Like escaping Edmonton for... well, bigger cities.',
          audioFile: '/media/Runaway Heart.mp3'
        },
        {
          title: 'High Rise Aspirations',
          duration: '3:33',
          description: 'An empowering song about reaching for the top and achieving personal goals. We\'re still working on the high rise part.',
          audioFile: '/media/High Rise Aspirations.mp3'
        },
        {
          title: 'Dust and Dreams',
          duration: '4:17',
          description: 'A laid-back track that explores the balance between reality and aspirations. Basically, it\'s about being broke but hopeful.',
          audioFile: '/media/Dust and Dreams.mp3'
        },
        {
          title: 'Final Call',
          duration: '3:52',
          description: 'A closing anthem that reflects on the journey and encourages moving forward into the unknown. Or at least to the next venue.',
          audioFile: '/media/Final Call.mp3'
        }
      ]
    }
  };

  const handlePlayPause = (albumKey, trackIndex) => {
    const trackId = `${albumKey}-${trackIndex}`;
    
    if (playingTrack === trackId) {
      // Pause current track
      audioRef.current?.pause();
      setPlayingTrack(null);
    } else {
      // Play new track
      if (audioRef.current) {
        audioRef.current.src = albums[albumKey].tracks[trackIndex].audioFile;
        audioRef.current.play();
        setPlayingTrack(trackId);
      }
    }
  };

  const handleAudioEnded = () => {
    setPlayingTrack(null);
  };

  const currentAlbum = albums[selectedAlbum];

  return (
    <div className="min-h-screen pt-20">
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        onEnded={handleAudioEnded}
        onPause={() => setPlayingTrack(null)}
      />
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl md:text-7xl font-bold bebas-neue gradient-text mb-6">
                DISCOGRAPHY
              </h1>
              <p className="text-xl text-gray-300 mb-6">
                Our collection of auditory masterpieces that prove we're not just another garage band from Edmonton. 
                We're THE garage band from Edmonton with two whole albums!
              </p>
              <p className="text-gray-400 mb-8">
                From the raw energy of "Super Deluxe" to the urban sophistication of "Urban Dreams", 
                these albums represent everything we stand for: loud guitars, funky basslines, tight drums, 
                and lyrics that range from profound to profoundly ridiculous.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://open.spotify.com/artist/1y4LSBAOXKcnWoCL0OgtHU"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 px-6 py-3 rounded-full font-semibold transition-colors"
                >
                  <SafeIcon icon={FiPlay} className="h-5 w-5" />
                  <span>Stream on Spotify</span>
                </a>
                <a
                  href="https://bigcitysupreme.bandcamp.com/album/super-deluxe"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 border border-yellow-500 hover:bg-yellow-500 hover:text-black px-6 py-3 rounded-full font-semibold transition-colors"
                >
                  <SafeIcon icon={FiExternalLink} className="h-5 w-5" />
                  <span>Buy on Bandcamp</span>
                </a>
              </div>
            </motion.div>
            
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="aspect-square bg-gradient-to-br from-red-600 to-yellow-600 rounded-lg p-8 glow-red">
                <div className="w-full h-full bg-black rounded-lg flex items-center justify-center relative overflow-hidden">
                  <div className="text-center z-10">
                    <div className="text-5xl font-bold bebas-neue gradient-text mb-2">
                      ALBUMS
                    </div>
                    <div className="text-3xl font-bold bebas-neue text-white">
                      2013-2018
                    </div>
                    <div className="text-sm text-gray-400 mt-4">
                      BIG CITY SUPREME
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-br from-red-600/10 to-yellow-600/10"></div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Album Selection */}
      <section className="py-12 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center gap-4">
            {Object.entries(albums).map(([key, album]) => (
              <button
                key={key}
                onClick={() => {
                  setSelectedAlbum(key);
                  setSelectedTrack(null);
                }}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                  selectedAlbum === key
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                {album.title}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Album Details */}
      <section className="py-20 bg-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12 items-start">
            {/* Album Cover */}
            <motion.div
              className="lg:col-span-1"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="aspect-square rounded-lg overflow-hidden glow-red">
                <img
                  src={currentAlbum.image}
                  alt={currentAlbum.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="mt-6 text-center">
                <h2 className="text-3xl font-bold bebas-neue gradient-text mb-2">
                  {currentAlbum.title}
                </h2>
                <p className="text-gray-400">
                  Released {currentAlbum.year}
                </p>
              </div>
            </motion.div>

            {/* Tracklist */}
            <motion.div
              className="lg:col-span-2"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-white mb-6">Tracklist</h3>
              <div className="space-y-4">
                {currentAlbum.tracks.map((track, index) => {
                  const trackId = `${selectedAlbum}-${index}`;
                  const isPlaying = playingTrack === trackId;
                  
                  return (
                    <motion.div
                      key={index}
                      className={`bg-black/50 rounded-lg border transition-all duration-300 ${
                        selectedTrack === index
                          ? 'border-red-500 bg-red-900/20'
                          : 'border-gray-700 hover:border-gray-600'
                      }`}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.05 }}
                      viewport={{ once: true }}
                      whileHover={{ scale: 1.02 }}
                    >
                      <div className="p-6">
                        <div className="flex items-center justify-between">
                          <div 
                            className="flex items-center space-x-4 flex-1 cursor-pointer"
                            onClick={() => setSelectedTrack(selectedTrack === index ? null : index)}
                          >
                            <div className="text-2xl font-bold text-red-400 bebas-neue w-8">
                              {String(index + 1).padStart(2, '0')}
                            </div>
                            <div className="flex-1">
                              <h4 className="text-xl font-semibold text-white mb-1">
                                {track.title}
                              </h4>
                              <div className="flex items-center space-x-2 text-gray-400 text-sm">
                                <SafeIcon icon={FiClock} className="h-4 w-4" />
                                <span>{track.duration}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePlayPause(selectedAlbum, index);
                              }}
                              className={`p-3 rounded-full transition-all ${
                                isPlaying
                                  ? 'bg-red-600 hover:bg-red-700 text-white'
                                  : 'bg-gray-800 hover:bg-gray-700 text-gray-300'
                              }`}
                              aria-label={isPlaying ? 'Pause' : 'Play'}
                            >
                              <SafeIcon
                                icon={isPlaying ? FiPause : FiPlay}
                                className="h-5 w-5"
                              />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTrack(selectedTrack === index ? null : index);
                              }}
                              className="p-2"
                            >
                              <SafeIcon
                                icon={FiMusic}
                                className={`h-6 w-6 transition-colors ${
                                  selectedTrack === index ? 'text-red-400' : 'text-gray-500'
                                }`}
                              />
                            </button>
                          </div>
                        </div>

                        {selectedTrack === index && (
                          <motion.div
                            className="mt-4 pt-4 border-t border-gray-700"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            <p className="text-gray-300 leading-relaxed">
                              {track.description}
                            </p>
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              <motion.div
                className="text-center mt-12"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <p className="text-gray-400 mb-6">
                  Total runtime: {currentAlbum.tracks.reduce((acc, track) => {
                    const [min, sec] = track.duration.split(':').map(Number);
                    return acc + (min * 60 + sec);
                  }, 0)} seconds of pure auditory supremacy
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="https://open.spotify.com/artist/1y4LSBAOXKcnWoCL0OgtHU"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 px-6 py-3 rounded-full font-semibold transition-colors"
                  >
                    <SafeIcon icon={FiPlay} className="h-5 w-5" />
                    <span>Listen Now</span>
                  </a>
                  <a
                    href="https://bigcitysupreme.bandcamp.com/album/super-deluxe"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 border border-yellow-500 hover:bg-yellow-500 hover:text-black px-6 py-3 rounded-full font-semibold transition-colors"
                  >
                    <SafeIcon icon={FiExternalLink} className="h-5 w-5" />
                    <span>Own It Forever</span>
                  </a>
                  <a
                    href="https://bigcitysupreme.com/covers/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 border border-red-500 hover:bg-red-500 hover:text-white px-6 py-3 rounded-full font-semibold transition-colors text-red-400"
                  >
                    <SafeIcon icon={FiExternalLink} className="h-5 w-5" />
                    <span>Our Favorite Covers</span>
                  </a>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            className="text-4xl md:text-5xl font-bold bebas-neue gradient-text text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            What The Critics Say
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                quote: "Gone for Good has that gritty, skateboard-crash-and-get-up attitude that makes you want to conquer the world (or at least your local skate park).",
                source: "SaltRock",
                track: "Gone for Good"
              },
              {
                quote: "Gold Dipped Diamonds is a fascinating blend of influences, combining funk and punk with swagger that would make the Beastie Boys proud.",
                source: "Music Review",
                track: "Gold Dipped Diamonds"
              },
              {
                quote: "Urban Dreams shows remarkable growth as a band, capturing the essence of city life with the same energy that made their debut so compelling.",
                source: "Rock Critic",
                track: "Urban Dreams"
              },
              {
                quote: "These songs have the potential to be hit singles, thanks to their catchy hooks and confident execution. Big City Supreme doesn't just play music - they own it.",
                source: "Indie403.com",
                track: "Super Deluxe"
              }
            ].map((review, index) => (
              <motion.div
                key={index}
                className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg p-6 border border-yellow-500/20"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <blockquote className="text-gray-300 italic mb-4 text-lg leading-relaxed">
                  "{review.quote}"
                </blockquote>
                <div className="flex justify-between items-center">
                  <cite className="text-yellow-400 font-semibold">
                    — {review.source}
                  </cite>
                  <span className="text-red-400 text-sm">
                    {review.track}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Music;